import { MNSproCloudApi } from '..';
import {
  AzureCourse,
  CourseDriveItemResponse,
  GroupRole,
  UserGroupResponse,
  UserGroupUpdateRequestBody,
  UserGroupUpdateRequestChangeType,
  Workgroup,
} from '../api/v2';

export class GroupService {
  /**
   * Gets the photo of a group.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} groupId
   * @return {*}
   * @memberof GroupService
   */
  static async getPhoto(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<string> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    const result = await api.getPhoto(groupId, organizationId);
    return result.data;
  }

  static async getCourse(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<AzureCourse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CoursesApiFactory(config);
    const result = await api.get(groupId, organizationId);
    return result.data;
  }

  static async getAllCourses(
    authToken: string,
    organizationId: string,
    timePeriod?: string,
  ): Promise<AzureCourse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CoursesApiFactory(config);
    const result = await api.getAllMemberships(organizationId);

    return result.data;
  }

  static async getAllCoursesV2(
    authToken: string,
    organizationId: string,
    timePeriod?: string,
  ): Promise<AzureCourse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CoursesApiFactory(config);
    const result = await api.getAllCombined(organizationId, timePeriod);

    return result.data;
  }

  static async getCourseMaterial(
    authToken: string,
    organizationId: string,
    courseId: string,
  ): Promise<CourseDriveItemResponse> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.CoursesApiFactory(config);
    const result = await api.getMaterial(courseId, organizationId);
    return result.data;
  }

  static async getUserGroups(
    authToken: string,
    organizationId: string,
  ): Promise<Workgroup[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.WorkgroupsApiFactory(config);
    const result = await api.getAll(organizationId);
    return result.data;
  }

  static async getAll(
    authToken: string,
    organizationId: string,
    timePeriod?: string,
  ): Promise<UserGroupResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    const result = await api.getAll(organizationId, undefined, undefined, timePeriod);
    return result.data;
  }

  static async getAllWithGroupRole(
    authToken: string,
    organizationId: string,
    groupRole: GroupRole,
  ): Promise<UserGroupResponse[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    const result = await api.getAll(organizationId, groupRole);
    return result.data;
  }

  static async addUsersToCourse(
    authToken: string,
    organizationId: string,
    courseId: string,
    courseName: string,
    userIds: string[],
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    const request: UserGroupUpdateRequestBody = {
      id: courseId,
      name: courseName,
      userUpdate: userIds.map(id => ({
        id,
        changeType: UserGroupUpdateRequestChangeType.user,
      })),
    };
    await api.update(organizationId, request);
  }

  static async removeMembers(
    authToken: string,
    organizationId: string,
    courseId: string,
    courseName: string,
    userIds: string[],
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    const request: UserGroupUpdateRequestBody = {
      id: courseId,
      name: courseName,
      userUpdate: userIds.map(id => ({
        id,
        changeType: UserGroupUpdateRequestChangeType.deleted,
      })),
    };
    await api.update(organizationId, request);
  }
}
